import React, { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { question_nl: "", question_fr: "", answer_nl: "", answer_fr: "", order: 0, active: true };

export default function AdminFAQ() {
  const [faq, setFaq] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/faq", { params: { include_inactive: true } }).then((r) => setFaq(r.data)).catch(() => setFaq([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, order: (faq?.length || 0) }); setOpen(true); };
  const openEdit = (f) => { setEditing(f); setForm(f); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) };
      if (editing) await api.put(`/faq/${editing.id}`, payload);
      else await api.post("/faq", payload);
      toast.success("FAQ opgeslagen"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const del = async (f) => { if (window.confirm("FAQ verwijderen?")) { await api.delete(`/faq/${f.id}`); load(); } };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">FAQ beheer</h1>
        <Button className="cta-orange rounded-full" onClick={openNew} data-testid="add-faq-btn"><Plus size={16} className="mr-1" /> Vraag toevoegen</Button>
      </div>

      {faq === null ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="mt-6 space-y-3">
          {faq.map((f) => (
            <div key={f.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`admin-faq-${f.id}`}>
              <div>
                <p className="font-medium text-slate-900">{f.question_nl}</p>
                <p className="text-sm text-slate-500 mt-1">{f.answer_nl}</p>
                {!f.active && <span className="text-xs text-red-500">Inactief</span>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(f)} data-testid={`edit-faq-${f.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(f)} data-testid={`delete-faq-${f.id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{editing ? "Vraag bewerken" : "Nieuwe vraag"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Vraag (NL)</Label><Input className="mt-1" value={form.question_nl} onChange={(e) => setForm({ ...form, question_nl: e.target.value })} data-testid="faq-q-nl" /></div>
            <div><Label>Antwoord (NL)</Label><Textarea className="mt-1" value={form.answer_nl} onChange={(e) => setForm({ ...form, answer_nl: e.target.value })} data-testid="faq-a-nl" /></div>
            <div><Label>Vraag (FR)</Label><Input className="mt-1" value={form.question_fr} onChange={(e) => setForm({ ...form, question_fr: e.target.value })} data-testid="faq-q-fr" /></div>
            <div><Label>Antwoord (FR)</Label><Textarea className="mt-1" value={form.answer_fr} onChange={(e) => setForm({ ...form, answer_fr: e.target.value })} data-testid="faq-a-fr" /></div>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="faq-active" /> Actief</label>
            <Button className="cta-orange rounded-full w-full" disabled={busy || !form.question_nl} onClick={save} data-testid="save-faq-btn">{busy ? <Loader2 className="animate-spin" /> : "Opslaan"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
